import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { updateArtisticStyle, updateSpace } from './actions'
import { selectArtisticStyle, selectQuery, selectSpace } from './reducer'
import { SPACE_OPTIONS, STYLE_OPTIONS } from './constants'
import {
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ToggleButton,
} from '@zoom/react-ui'
import { IconChevronDownV2, IconNone } from '@zoom/icons-react'
import './NewPresets.css'

const NewPresets = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [isSpaceOpen, setIsSpaceOpen] = useState(false)
  const [isStyleOpen, setIsStyleOpen] = useState(false)
  const selectedStyle = useSelector(selectArtisticStyle)
  const query = useSelector(selectQuery)
  const selectedSpaceId = useSelector(selectSpace)
  const trimmedQuery = query?.trim()
  const hasStyleInput = Boolean(selectedSpaceId || query?.trim())
  const containerRef = useRef(null)
  const selectedSpace = SPACE_OPTIONS.find(
    (option) => option.id === selectedSpaceId
  )
  const selectedStyleOption = STYLE_OPTIONS.find(
    (option) => option.value === selectedStyle
  )

  useEffect(() => {
    const onDocumentClick = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsSpaceOpen(false)
        setIsStyleOpen(false)
      }
    }

    document.addEventListener('mousedown', onDocumentClick)
    return () => {
      document.removeEventListener('mousedown', onDocumentClick)
    }
  }, [])

  useEffect(() => {
    if (!hasStyleInput) {
      setIsStyleOpen(false)
      if (selectedStyle) {
        dispatch(updateArtisticStyle(null))
      }
    }
  }, [dispatch, hasStyleInput, selectedStyle])

  const handleSelectSpace = (option) => {
    const nextSpaceId = option.id === 'none' ? null : option.id
    dispatch(updateSpace(nextSpaceId))

    if (!nextSpaceId && !trimmedQuery) {
      dispatch(updateArtisticStyle(null))
      setIsStyleOpen(false)
    }

    setIsSpaceOpen(false)
  }

  const handleSelectStyle = (styleOption) => {
    dispatch(updateArtisticStyle(styleOption.value))
    setIsStyleOpen(false)
  }

  const handleClearStyle = () => {
    dispatch(updateArtisticStyle(null))
    setIsStyleOpen(false)
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setIsSpaceOpen(false)
      setIsStyleOpen(false)
    }
  }

  return (
    <div className="new-presets" ref={containerRef} onKeyDown={handleKeyDown}>
      <div className="new-presets-control">
        <ToggleButton
          className={`new-presets-button ${selectedSpace ? 'selected' : ''}`}
          variant="label"
          selected={Boolean(selectedSpace)}
          aria-expanded={isSpaceOpen}
          aria-haspopup="listbox"
          endIcon={
            <IconChevronDownV2
              style={{ display: 'inline-flex', lineHeight: 1, fontSize: 18 }}
            />
          }
          onClick={() => {
            setIsSpaceOpen((prev) => !prev)
            setIsStyleOpen(false)
          }}
        >
          {selectedSpace ? (
            <span className="new-presets-button-content">
              {selectedSpace.thumbnail ? (
                <img
                  src={selectedSpace.thumbnail}
                  alt=""
                  aria-hidden="true"
                  className="new-presets-button-thumb"
                />
              ) : null}
              <span>{t(selectedSpace.label)}</span>
            </span>
          ) : (
            t('Select a space')
          )}
        </ToggleButton>
        {isSpaceOpen && (
          <List className="new-presets-menu" role="listbox">
            <ListItem
              className="new-presets-menu-item"
              key="none-space"
              role="option"
              aria-selected={selectedSpaceId === null}
              selected={selectedSpaceId === null}
              onClick={() => handleSelectSpace({ id: 'none', prompt: '' })}
            >
              <ListItemAvatar>
                <IconNone
                  style={{
                    display: 'flex',
                    fontSize: 26,
                    justifyContent: 'center',
                    padding: 3,
                  }}
                />
              </ListItemAvatar>
              <ListItemText primary={t('None')} />
            </ListItem>
            {SPACE_OPTIONS.map((option) => (
              <ListItem
                className="new-presets-menu-item"
                key={option.id}
                role="option"
                aria-selected={selectedSpaceId === option.id}
                selected={selectedSpaceId === option.id}
                onClick={() => handleSelectSpace(option)}
              >
                <ListItemAvatar>
                  {option.thumbnail ? (
                    <img
                      src={option.thumbnail}
                      alt=""
                      aria-hidden="true"
                      className="new-presets-menu-item-thumb"
                    />
                  ) : (
                    <span
                      className="new-presets-menu-item-thumb new-presets-menu-item-thumb-fallback"
                      aria-hidden="true"
                    >
                      {option.label === 'None'
                        ? '\u00D8'
                        : option.label.charAt(0).toUpperCase()}
                    </span>
                  )}
                </ListItemAvatar>
                <ListItemText primary={t(option.label)} />
              </ListItem>
            ))}
          </List>
        )}
      </div>

      <div className="new-presets-control">
        <ToggleButton
          className={`new-presets-button ${!hasStyleInput ? 'disabled' : ''} ${
            selectedStyleOption ? 'selected' : ''
          }`}
          variant="label"
          disabled={!hasStyleInput}
          selected={Boolean(selectedStyle)}
          aria-expanded={isStyleOpen}
          aria-haspopup="listbox"
          endIcon={<IconChevronDownV2 />}
          onClick={() => {
            if (!hasStyleInput) return
            setIsStyleOpen((prev) => !prev)
            setIsSpaceOpen(false)
          }}
        >
          {selectedStyleOption ? (
            <span className="new-presets-button-content">
              {selectedStyleOption.thumbnail ? (
                <img
                  src={selectedStyleOption.thumbnail}
                  alt=""
                  aria-hidden="true"
                  className="new-presets-button-thumb"
                />
              ) : null}
              <span>{t(selectedStyleOption.value)}</span>
            </span>
          ) : (
            t('Select style')
          )}
        </ToggleButton>

        {isStyleOpen && (
          <List className="new-presets-menu" role="listbox">
            <ListItem
              className="new-presets-menu-item"
              key="none-style"
              role="option"
              aria-selected={!selectedStyle}
              selected={!selectedStyle}
              onClick={handleClearStyle}
            >
              <ListItemAvatar>
                <IconNone
                  style={{
                    display: 'flex',
                    fontSize: 26,
                    justifyContent: 'center',
                    padding: 3,
                  }}
                />
              </ListItemAvatar>
              <ListItemText primary={t('None')} />
            </ListItem>
            {STYLE_OPTIONS.map((styleOption) => (
              <ListItem
                className="new-presets-menu-item"
                key={styleOption.value}
                role="option"
                aria-selected={selectedStyle === styleOption.value}
                selected={selectedStyle === styleOption.value}
                onClick={() => handleSelectStyle(styleOption)}
              >
                <ListItemAvatar>
                  {styleOption.thumbnail ? (
                    <img
                      src={styleOption.thumbnail}
                      alt=""
                      aria-hidden="true"
                      className="new-presets-menu-item-thumb"
                    />
                  ) : (
                    <span
                      className="new-presets-menu-item-thumb new-presets-menu-item-thumb-fallback"
                      aria-hidden="true"
                    >
                      {styleOption.value.charAt(0).toUpperCase()}
                    </span>
                  )}
                </ListItemAvatar>
                <ListItemText primary={t(styleOption.value)} />
              </ListItem>
            ))}
          </List>
        )}
      </div>
    </div>
  )
}

export default NewPresets
